// src/config/learningTips.ts

import { LearningTip, FeatureId } from "../interfaces/learningTipTypes";
import { FEATURE_FLAGS } from "./featureFlags";

/**
 * Compact tip definitions - content strings only.
 *
 * Tips follow UX microcopy best practices:
 * - Always provide context (feature name + location)
 * - Use action verbs (Click, Try, Open, Enable)
 * - Explain the benefit (why users should care)
 * - Standalone clarity (each tip makes sense on its own)
 * - Conversational but instructional tone
 *
 * @see https://www.smashingmagazine.com/2024/06/how-improve-microcopy-ux-writing-tips-non-ux-writers/
 */

// ============================================
// Feature Tips - Shared across ALL agents
// ============================================

const FEATURE_TIP_CONTENT: Record<FeatureId, string[]> = {
  thinking: [
    // Enable tips
    "Enable extended thinking to see my reasoning process and verify my analytical approach",
    "Enable extended thinking for multi-layered indemnity or liability allocation analysis",
    "Enable extended thinking when assessing competing bids against complex technical specifications",
    "Enable extended thinking for identifying conflicts across multiple interconnected contract clauses",
    "Enable extended thinking when evaluating risk exposure in non-standard commercial terms",
    "Enable extended thinking for pricing structures with conditional escalations or complex calculations",
    // Disable tips
    "Disable extended thinking for quick contract term definitions or standard clause explanations",
    "Disable extended thinking when extracting basic data like dates, parties, or values",
    "Disable extended thinking for straightforward redlining with clear, simple instructions",
    "Disable extended thinking for formatting existing content or generating standard templates",
  ],
  benchmark: [
    "Enable Benchmark Mode to get World Commerce and Contracting industry best practices negotiation positions",
    "Enable Benchmark Mode to see how your terms compare to market standards based on World Commerce and Contracting research",
    "Use Benchmark Mode to compare your contract terms against industry standards based on World Commerce and Contracting research",
    "Enable Benchmark Mode to support your negotiation position",
    "Enable Benchmark Mode to see whether counterparty requests are standard or unusual",
  ],
  "web-search": [
    "Enable Search the Web to research your counterparty",
    "Enable Search the Web for current regulations, case law, and market data",
    "Enable Search the Web for questions about current regulations",
    "Enable Search the Web to find recent case law and legal updates",
    "Enable Search the Web to verify company details and registrations",
    "Enable Search the Web to check jurisdiction-specific requirements in real time",
    "Enable Search the Web to retrieve the latest regulatory guidance",
    "Enable Search the Web to confirm effective dates of new legislation",
  ],
  "auto-redline": [
    "Ask any agent to edit, revise, or rewrite content directly in your document",
    "Request edits to specific clauses — our agents will find and modify them for you",
    "Any agent can edit text inside tables, including nested tables",
    "Ask any agent to rewrite bullet points or numbered lists",
    "Agents can only edit cells with existing content — add placeholder text to empty cells first",
    "Agents can only see changes that are shown as redlines. If you have strikethroughs in your document, the agent won't see it as a change",
    "Remember to turn on Track Changes so the agent can create redlines for you",
  ],
  "prompt-coach": [
    "Click Prompt Coach's Next Steps for ready-to-use follow up prompts",
    "Prompt Coach's Next Steps are ready-to-use prompts — just click and send",
    "Prompt Coach's Strategic Insight reveals gaps that could improve results",
    "Check Prompt Coach's Strategic Insight for deeper insights",
    "Have a look at Prompt Enhancements to learn useful prompting techniques",
  ],
  "parallel-agents": [
    "Work with multiple agents simultaneously in separate chats to work 4 times faster",
    "Switch agents freely — your conversation is preserved",
    "Compare insights by asking the same question to multiple agents in parallel",
    "Each agent keeps its own chat session — switch freely without losing context",
  ],
  "file-upload": [
    "Drag and drop files directly into the chat to provide additional context",
    "Drag and drop Word, Excel, or PDF files as additional context",
    "Add reference documents or playbooks by dragging and dropping, to help agents understand your standards",
    "Upload your negotiation playbook for context-aware suggestions",
    "Share company policies to augment the agent's response with your company's compliance posture",
  ],
  "copy-button": [
    "When you copy and paste the agent's response, tables, lists, and text formatting are preserved",
    "Copied content pastes into Word with formatting intact — no cleanup needed",
  ],
  feedback: [
    "Use thumbs up/down on responses to tell us what you think of a response",
    "Your feedback—positive and negative helps us shape future updates",
    "We read every feedback message, so give us your honest thoughts",
    "Tell us why a response helped or fell short using thumbs up/down and the feedback box",
  ],
  sharepoint: [
    "Type @documentname in chat to easily reference a SharePoint document",
    "Easily attach SharePoint files as additional context by calling it @documentname",
    "Click the + button and Browse Document Library to select multiple files",
    "Drag and drop files into Document Library to add them to SharePoint",
    "Click any attached SharePoint file to open it in your browser",
    "Access your team's SharePoint documents without leaving Contract Space",
  ],
  transparency: [
    "Always review answers of agents. They are not experts like you.",
    "Always check links and benchmarks to make sure they are correct and appropriate to your use case",
    "Try asking the agent if it has all the information it needs before it begins its analysis",
    "Try asking the agent to write a plan for you before it begins its analysis",
    "If an agent makes a mistake, talk to it like you would a junior colleague to find out where it went wrong",
  ],
};

// ============================================
// Agent Tips - Specific to each agent
// Tips explain: when to use, what documents help, key capabilities
// NOT example prompts - focus on understanding the agent's purpose
// ============================================

const AGENT_TIP_CONTENT: Record<string, string[]> = {
  "ccm-space": [
    "General CCM Space is your go-to for general contracting questions",
    "General CCM Space works best with additional context documents uploaded",
    "Use General CCM Space for bid evaluation and SOW analysis",
    "General CCM Space is ideal when no specialized agent fits your need",
    "General CCM Space covers the full contracting lifecycle",
  ],
  "red-line-analyzer": [
    "Red-Line Analyzer reviews tracked changes BEFORE you sign",
    "Upload your standard template to Red-Line Analyzer for comparison",
    "Red-Line Analyzer helps draft counter-language for negotiations",
    "Red-Line Analyzer helps you build a strong negotiation position",
  ],
  "contract-qa": [
    "Contract Q&A helps you manage already-signed agreements",
    "Upload your standard template to Contract Q&A for comparison",
    "Contract Q&A tracks compliance, obligations, and deadlines",
    "Contract Q&A identifies risk exposure in signed contracts",
    "Contract Q&A helps stakeholders understand their responsibilities",
  ],
  "drafting-assistant": [
    "Drafting Assistant creates SOWs, RFPs, and approval documents",
    "Upload templates to Drafting Assistant for better results",
    "Drafting Assistant marks gaps with [FILL ME] placeholders for easy review",
    "Drafting Assistant can refine existing content or draft new",
    "Drafting Assistant shows assumptions made — so you can easily review them",
  ],
};

// ============================================
// Expand compact definitions into LearningTip objects
// IDs are auto-generated for tracking seen/unseen status
// ============================================

function expandFeatureTips(): LearningTip[] {
  return Object.entries(FEATURE_TIP_CONTENT).flatMap(([featureId, contents]) =>
    contents.map((content, index) => ({
      id: `${featureId}-${index + 1}`,
      featureId: featureId as FeatureId,
      content,
    }))
  );
}

function expandAgentTips(): LearningTip[] {
  return Object.entries(AGENT_TIP_CONTENT).flatMap(([agentId, contents]) =>
    contents.map((content, index) => ({
      id: `${agentId}-${index + 1}`,
      agentId,
      content,
    }))
  );
}

/** All feature-based tips (61 total across 11 feature categories) */
export const FEATURE_TIPS: LearningTip[] = expandFeatureTips();

/** All agent-specific tips (20 total: 5 per agent × 4 agents) */
export const AGENT_TIPS: LearningTip[] = expandAgentTips();

// ============================================
// Public API functions
// ============================================

/**
 * Parse DISABLED_TIP_FEATURES into a Set for efficient lookups.
 * Used by multiple functions to check disabled features/agents.
 */
function getDisabledSet(): Set<string> {
  const disabled = FEATURE_FLAGS.DISABLED_TIP_FEATURES;
  if (disabled === "none") return new Set();

  return new Set(
    disabled
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0)
  );
}

/**
 * Check if all agent tips are disabled via 'all-agents' keyword.
 */
function areAgentTipsDisabled(): boolean {
  return getDisabledSet().has("all-agents");
}

/**
 * Get enabled feature tips based on DISABLED_TIP_FEATURES setting.
 * Respects the environment variable to filter out disabled features.
 */
export function getEnabledFeatureTips(): LearningTip[] {
  const disabled = FEATURE_FLAGS.DISABLED_TIP_FEATURES;

  // 'none' means all feature tips are enabled
  if (disabled === "none") {
    return FEATURE_TIPS;
  }

  // 'all' means no feature tips (only agent tips will show)
  if (disabled === "all") {
    return [];
  }

  // Use shared parsing for disabled feature IDs
  const disabledSet = getDisabledSet();

  return FEATURE_TIPS.filter((tip) => !disabledSet.has(tip.featureId as string));
}

/**
 * Get all available tips for a specific agent.
 * Combines enabled feature tips (shared) + agent-specific tips.
 * Respects 'all-agents' keyword in DISABLED_TIP_FEATURES to hide agent tips.
 */
export function getAvailableTips(agentId: string): LearningTip[] {
  const enabledFeatureTips = getEnabledFeatureTips();

  // Skip agent tips if 'all-agents' is in disabled list
  if (areAgentTipsDisabled()) {
    return enabledFeatureTips;
  }

  const agentSpecificTips = AGENT_TIPS.filter((tip) => tip.agentId === agentId);
  return [...enabledFeatureTips, ...agentSpecificTips];
}

/**
 * Get all unique agent IDs that have tips defined.
 */
export function getAgentIdsWithTips(): string[] {
  return Object.keys(AGENT_TIP_CONTENT);
}

/**
 * Get all unique feature IDs that have tips defined.
 */
export function getFeatureIdsWithTips(): FeatureId[] {
  return Object.keys(FEATURE_TIP_CONTENT) as FeatureId[];
}

/**
 * Get total count of tips per agent.
 */
export function getTipCountByAgent(): Map<string, number> {
  return new Map(Object.entries(AGENT_TIP_CONTENT).map(([id, tips]) => [id, tips.length]));
}

/**
 * Get total count of tips per feature.
 */
export function getTipCountByFeature(): Map<FeatureId, number> {
  return new Map(
    Object.entries(FEATURE_TIP_CONTENT).map(([id, tips]) => [id as FeatureId, tips.length])
  );
}
